import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Slide from 'rc-slider';
import { dataHooks } from './constants';
import { generateID } from '../utils/generateId';
import SliderHandle from './SliderHandle';
import Text from '../Text';
import { st, classes } from './WsrSlider.st.css';
import { getRailStyle } from './utils';
const range = ({ min, max, step }) => {
    const arr = [];
    for (let i = min; i <= max; i += step) {
        arr.push(i);
    }
    return arr;
};
/**
 * A slider component with multi-range support
 */
export default class Slider extends Component {
    constructor() {
        super(...arguments);
        this._renderHandle = handleProps => {
            const { gradientColor, displayTooltip, disabled, marks, dataHook, direction, onFocus, onBlur, enhanceValue, } = this.props;
            const { index, value, min, max, offset, ref, ariaLabel, reverse } = handleProps;
            let tooltipValue = enhanceValue?.(value) ?? value.toString();
            if (this._isCustomMarks()) {
                if (marks.hasOwnProperty(value)) {
                    tooltipValue = marks[value].toString();
                }
                else {
                    tooltipValue = enhanceValue?.(value);
                }
            }
            return (React.createElement(SliderHandle, { key: index, gradientColor: gradientColor, disabled: disabled, displayTooltip: displayTooltip, tooltipValue: tooltipValue, handleProps: {
                    ref,
                    offset,
                    min,
                    max,
                    value,
                    reverse,
                    ariaLabel,
                }, index: index, dataHook: dataHook, direction: direction, onBlurCustom: onBlur, onFocusCustom: onFocus }));
        };
        this._renderSlider = () => {
            const { gradientColor, onFocus, onBlur, pushable, allowCross, value, displayMarks, direction, startPoint, dataHook, id, ariaLabelForHandle, rtl, className, disabled, ...rest } = this.props;
            return Array.isArray(value) && value.length > 1 ? (React.createElement(Slide.Range, { ...rest, disabled: disabled, vertical: direction === 'vertical', railStyle: getRailStyle({ gradientColor, disabled, direction }), prefixCls: "wsr-slider", handle: this._renderHandle, marks: displayMarks ? this._getMarks() : {}, value: value, pushable: pushable, allowCross: allowCross, reverse: rtl, ariaLabelGroupForHandles: ariaLabelForHandle })) : (React.createElement(Slide, { ...rest, disabled: disabled, vertical: direction === 'vertical', railStyle: getRailStyle({ gradientColor, disabled, direction }), prefixCls: "wsr-slider", startPoint: startPoint, handle: this._renderHandle, marks: displayMarks ? this._getMarks() : {}, value: Array.isArray(value) ? value[0] : value, reverse: rtl, ariaLabelForHandle: Array.isArray(ariaLabelForHandle)
                    ? ariaLabelForHandle[0]
                    : ariaLabelForHandle }));
        };
    }
    _getMarks() {
        const marksLabels = {};
        if (this._isCustomMarks()) {
            const { marks } = this.props;
            Object.entries(marks).forEach(([key, value]) => {
                marksLabels[key] = {
                    label: this._createMarkNode(value, true),
                };
            });
        }
        else {
            const { min, max, step, startPoint } = this.props;
            range({ min, max, step }).forEach(entry => {
                const shouldRenderMarkLabel = entry === min || entry === max || entry === startPoint;
                marksLabels[entry] = {
                    label: this._createMarkNode(this.props.enhanceValue?.(entry) ?? entry, shouldRenderMarkLabel),
                };
            });
        }
        return marksLabels;
    }
    _isCustomMarks() {
        const { marks } = this.props;
        return Object.entries(marks).length > 0;
    }
    _createMarkNode(value, shouldRenderMarkLabel) {
        return (React.createElement("div", { className: st(classes.mark, { direction: this.props.direction }) },
            React.createElement("div", { className: classes.markLine }),
            React.createElement("div", { className: classes.markValue }, shouldRenderMarkLabel && (React.createElement(Text, { className: classes.markLabel, dataHook: dataHooks.sliderMarkLabel, weight: "thin", size: "small" }, value)))));
    }
    render() {
        const { dataHook, id, direction, className, gradientColor } = this.props;
        const hasGradient = !!gradientColor;
        const horizontal = direction !== 'vertical';
        return (React.createElement("div", { className: st(classes.root, { hasGradient, horizontal }, className), id: id, "data-hook": dataHook, "data-direction": direction }, this._renderSlider()));
    }
}
Slider.displayName = 'Slider';
Slider.propTypes = {
    allowCross: PropTypes.bool,
    dataHook: PropTypes.string,
    className: PropTypes.string,
    displayMarks: PropTypes.bool,
    displayTooltip: PropTypes.bool,
    id: PropTypes.string,
    max: PropTypes.number,
    min: PropTypes.number,
    // @ts-expect-error
    marks: PropTypes.object,
    onAfterChange: PropTypes.func,
    onBeforeChange: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    rtl: PropTypes.bool,
    step: PropTypes.number,
    pushable: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    value: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.number),
        PropTypes.number,
    ]),
    enhanceValue: PropTypes.func,
    disabled: PropTypes.bool,
    startPoint: PropTypes.number,
    direction: PropTypes.oneOf(['vertical', 'horizontal']),
    ariaLabelForHandle: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string,
    ]),
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    gradientColor: PropTypes.string,
};
Slider.defaultProps = {
    min: 1,
    max: 20,
    step: 1,
    value: 3,
    allowCross: true,
    id: generateID(),
    displayTooltip: true,
    displayMarks: true,
    marks: {},
    rtl: false,
    disabled: false,
    startPoint: undefined,
};
//# sourceMappingURL=Slider.js.map